import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

// Convert #AARRGGBB hex to CSS rgba()
function argbToCss(hex: string | null | undefined, fallback = 'rgba(0,0,0,0)'): string {
  if (!hex) return fallback;
  const c = hex.replace('#', '');
  if (c.length !== 8) return fallback;
  const a = (parseInt(c.slice(0, 2), 16) / 255).toFixed(2);
  const r = parseInt(c.slice(2, 4), 16);
  const g = parseInt(c.slice(4, 6), 16);
  const b = parseInt(c.slice(6, 8), 16);
  return `rgba(${r},${g},${b},${a})`;
}

const PreviewShell = styled.div`
  width: 280px;
  height: 480px;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  position: relative;
  flex-shrink: 0;
`;

const BackgroundColorLayer = styled.div<{ bgColor: string }>`
  position: absolute;
  inset: 0;
  background-color: ${({ bgColor }) => bgColor};
`;

// Preview viewport size; the main (center) tile fills ~90% of it.
const SHELL_W = 280;
const SHELL_H = 480;
const FILL = 0.85;

const GridWrapper = styled.div<{
  cols: number;
  rows: number;
  tileW: number;
  tileH: number;
}>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: grid;
  grid-template-columns: repeat(${({ cols }) => cols}, ${({ tileW }) => tileW}px);
  grid-template-rows: repeat(${({ rows }) => rows}, ${({ tileH }) => tileH}px);
`;

// Each tile matches the image's aspect ratio exactly, so `100% 100%` shows the
// full image with no crop and no distortion, and adjacent tiles touch.
const Tile = styled.div<{
  bgImage: string;
  tileW: number;
  tileH: number;
  flipX?: boolean;
  flipY?: boolean;
}>`
  width: ${({ tileW }) => tileW}px;
  height: ${({ tileH }) => tileH}px;
  background-image: url(${({ bgImage }) => bgImage});
  background-size: 100% 100%;
  background-repeat: no-repeat;
  transform: scale(${({ flipX }) => (flipX ? -1 : 1)}, ${({ flipY }) => (flipY ? -1 : 1)});
`;

const PreviewBackground: React.FC<{
  bgColor: string;
  bgImage?: string | null;
  repeatMode: string;
}> = ({ bgColor, bgImage, repeatMode }) => {
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    setNatural(null);
    if (!bgImage) return;
    const img = new Image();
    img.onload = () => setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = bgImage;
  }, [bgImage]);

  let grid: React.ReactNode = null;
  if (bgImage && natural) {
    // Scale the image to fill ~90% of the shell while preserving aspect ratio.
    const scale = Math.min((FILL * SHELL_W) / natural.w, (FILL * SHELL_H) / natural.h);
    const tileW = natural.w * scale;
    const tileH = natural.h * scale;

    // Enough tiles around the center to cover the whole shell (+1 extra each
    // side so a partial neighbour is always visible at every edge).
    const extraCols = Math.max(1, Math.ceil((SHELL_W / 2 - tileW / 2) / tileW) + 1);
    const extraRows = Math.max(1, Math.ceil((SHELL_H / 2 - tileH / 2) / tileH) + 1);
    const cols = 2 * extraCols + 1;
    const rows = 2 * extraRows + 1;
    const centerCol = extraCols;
    const centerRow = extraRows;
    const isMirror = repeatMode !== 'repeat';

    grid = (
      <GridWrapper cols={cols} rows={rows} tileW={tileW} tileH={tileH}>
        {Array.from({ length: rows }, (_, row) =>
          Array.from({ length: cols }, (_, col) => (
            <Tile
              key={`${row}-${col}`}
              bgImage={bgImage}
              tileW={tileW}
              tileH={tileH}
              // Mirror outward from the unflipped center tile.
              flipX={isMirror && Math.abs(col - centerCol) % 2 === 1}
              flipY={isMirror && Math.abs(row - centerRow) % 2 === 1}
            />
          )),
        )}
      </GridWrapper>
    );
  }

  return (
    <>
      <BackgroundColorLayer bgColor={bgColor} />
      {grid}
    </>
  );
};

const Content = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ButtonsBar = styled.div<{ bgColor: string }>`
  height: 52px;
  background: ${({ bgColor }) => bgColor};
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0 12px;
  flex-shrink: 0;
`;

const MockIcon = styled.div<{ color: string; isActive?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${({ color }) => color};
  opacity: ${({ isActive }) => (isActive ? 1 : 0.55)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IconDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.6);
`;

const BoardArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const BoardRect = styled.div<{ bgColor: string; bgImage?: string | null }>`
  width: 62%;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  background-color: ${({ bgColor }) => bgColor};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  ${({ bgImage }) =>
    bgImage
      ? css`
          background-image: url(${bgImage});
          background-size: cover;
          background-position: center;
        `
      : ''}
`;

const BoardGrid = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 2px;
  padding: 6px;
`;

const PuzzlePiece = styled.div`
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
`;

const Scroller = styled.div<{ bgColor: string }>`
  height: 72px;
  background: ${({ bgColor }) => bgColor};
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  flex-shrink: 0;
  overflow: hidden;
`;

const ScrollerPiece = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.15);
`;

const RepeatBadge = styled.div`
  position: absolute;
  top: 56px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.6px;
  padding: 2px 6px;
  border-radius: 10px;
  z-index: 2;
`;

export interface ThemePreviewProps {
  gameplayBackgroundColor?: string | null;
  gameplayBackgroundImageUrl?: string | null;
  boardBackgroundColor?: string | null;
  boardBackgroundImageUrl?: string | null;
  scrollerColor?: string;
  buttonsBackgroundColor?: string;
  buttonsIconsColor?: string;
  buttonsActiveIconsColor?: string | null;
  gameplayBackgroundRepeatMode?: { value: string; label: string } | null;
}

const ICON_COUNT = 5;

const ThemePreview: React.FC<ThemePreviewProps> = ({
  gameplayBackgroundColor,
  gameplayBackgroundImageUrl,
  boardBackgroundColor,
  boardBackgroundImageUrl,
  scrollerColor,
  buttonsBackgroundColor,
  buttonsIconsColor,
  buttonsActiveIconsColor,
  gameplayBackgroundRepeatMode,
}) => {
  const bgCss = argbToCss(gameplayBackgroundColor, '#2a2a3e');
  const boardCss = argbToCss(boardBackgroundColor, '#1e2d3d');
  const scrollerCss = argbToCss(scrollerColor, '#1a1a2e');
  const buttonsBgCss = argbToCss(buttonsBackgroundColor, '#252535');
  const iconsColor = argbToCss(buttonsIconsColor, 'rgba(255,255,255,0.5)');
  const activeIconColor = argbToCss(
    buttonsActiveIconsColor || buttonsIconsColor,
    'rgba(255,255,255,0.9)',
  );
  const repeatMode = gameplayBackgroundRepeatMode?.value ?? 'mirror';

  return (
    <PreviewShell>
      <PreviewBackground
        bgColor={bgCss}
        bgImage={gameplayBackgroundImageUrl}
        repeatMode={repeatMode}
      />
      {gameplayBackgroundImageUrl && <RepeatBadge>{repeatMode.toUpperCase()}</RepeatBadge>}
      <Content>
        <ButtonsBar bgColor={buttonsBgCss}>
          {Array.from({ length: ICON_COUNT }).map((_, i) => (
            <MockIcon key={i} color={i === 2 ? activeIconColor : iconsColor} isActive={i === 2}>
              <IconDot />
            </MockIcon>
          ))}
        </ButtonsBar>
        <BoardArea>
          <BoardRect bgColor={boardCss} bgImage={boardBackgroundImageUrl}>
            <BoardGrid>
              {Array.from({ length: 9 }).map((_, i) => (
                <PuzzlePiece key={i} />
              ))}
            </BoardGrid>
          </BoardRect>
        </BoardArea>
        <Scroller bgColor={scrollerCss}>
          {Array.from({ length: 5 }).map((_, i) => (
            <ScrollerPiece key={i} />
          ))}
        </Scroller>
      </Content>
    </PreviewShell>
  );
};

export default ThemePreview;
